import * as Sentry from '@sentry/browser';

import React from 'react';
import PropTypes from 'prop-types';

import Organization from '../../models/organization';

import { ToastContext, ViewPort, MenuContainer } from '@weezevent/nacre';

import useAppContext from '../../context/useAppContext';
import { useTranslation } from 'react-i18next';
import useMixpanel from '../../context/useMixpanel';
import { usePermissions } from '../../user/permissions';

import Loading from '../loading';
import EventModel from '../../models/eventModel';
import MinisiteManager from '../../pages/minisite/index';
import TicketLogo from '../../nacre/images/weezticket.svg';
import TopBarModules from '../template/topBarModules/topBarModules';
import PageError from '../../pages/error/pageError';
import { getMenhirUrl } from '../../utils/config';

export default function MinisiteRouter({ organizationId, eventId, history, match }) {
    const permissions = usePermissions();
    const { t } = useTranslation();
    const [context, setContext] = useAppContext();
    const [errorCode, setErrorCode] = React.useState(null);
    const toast = React.useContext(ToastContext);
    const mixpanel = useMixpanel();

    const loadEvent = React.useCallback(
        (cache = true) => {
            return EventModel.get(
                { id: eventId, organizationId: organizationId },
                {
                    sync: 'True',
                    cache: cache,
                    allow_cache: cache
                }
            )
                .then(event => {
                    setContext({ event, reloadEvent: loadEvent });

                    Sentry.configureScope(scope => {
                        scope.setExtra('event_id', eventId);
                    });

                    mixpanel.register({
                        'Event ID': eventId
                    });
                })
                .catch(e => {
                    setErrorCode(e.code);
                    console.error(e);
                    toast.error(t('ticket.event.retrievalError'));
                });
        },
        [eventId, mixpanel, organizationId]
    );

    React.useEffect(() => {
        // Organization is already fetched
        if (context.organization && context.organization.id === organizationId) {
            return;
        }

        Organization.get({ id: organizationId }, { sync: 'True' })
            .then(response => {
                const organization = new Organization(response);
                setContext({ organization });
                loadEvent();

                // Set current orga
                Sentry.configureScope(scope => {
                    scope.setExtra('organization_id', organizationId);
                });

                mixpanel.register({
                    'Organizer ID': organizationId,
                    Application: 'WeezTicket',
                    'Event ID': eventId,
                    'Accounts ID': context?.connectData?.payload?.data?.user_id
                });
            })
            .catch(e => {
                setErrorCode(e.code);
                console.error(e);
                toast.error(t('ticket.router.error.organizationRetrieval'));
            });
    }, [organizationId, mixpanel]);

    if ((!context.organization || !context.event) && !errorCode) {
        return <Loading />;
    }

    if (!errorCode && !permissions.hasCapacity('GeminoCapacity.OWNER', organizationId)) {
        setErrorCode(403);
    }

    const menhirUrl = getMenhirUrl();
    const backHomeUrl = menhirUrl ? `${menhirUrl}/bo/` : '/';

    return (
        <ViewPort logo={TicketLogo} onClickLogo={null} topBarModules={<TopBarModules organization={context.organization} minimal />}>
            {errorCode ? (
                <MenuContainer display={false}>
                    <PageError errorCode={errorCode} backHomeUrl={backHomeUrl} />
                </MenuContainer>
            ) : (
                <MinisiteManager event={context.event} organization={context.organization} standalone autoCreate />
            )}
        </ViewPort>
    );
}

MinisiteRouter.propTypes = {
    organizationId: PropTypes.string.isRequired,
    eventId: PropTypes.string.isRequired,
    history: PropTypes.object.isRequired,
    match: PropTypes.object.isRequired
};
